import { Description, AudioFile, PictureAsPdf } from "@mui/icons-material";
import { format } from "date-fns";

/** Format date as YYYYMMDD for file naming (e.g., 20260130) */
export const formatDateForFileName = (date: Date | string): string => {
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    return format(d, "yyyyMMdd");
  } catch {
    return "";
  }
};

/** Format date and time for display (e.g., "12/12/2025 19:32:15") */
export const formatDateTimeForDisplay = (date: Date | string): string => {
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    return format(d, "MM/dd/yyyy HH:mm:ss");
  } catch {
    return "N/A";
  }
};

/** Format date for file display (e.g., "Aug 20 - 10:00") */
export const formatDateForFileDisplay = (date: Date | string): string => {
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    return format(d, "MMM dd - HH:mm");
  } catch {
    return "N/A";
  }
};

/** Generate file name (e.g., "JD12302025.docx") */
export const generateFileName = (
  abbreviation: string,
  dateStr: string,
  fileType: "docx" | "mp3" | "pdf",
  suffix?: number,
): string => {
  const suffixStr = suffix ? ` (${suffix})` : "";
  return `${abbreviation}${dateStr}${suffixStr}.${fileType}`;
};

/** Format day header: "Today", "Yesterday", or "MMM dd, yyyy" */
export const formatDayHeader = (date: Date): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return format(date, "MMM dd, yyyy");
};

/** Download file blob with correct filename */
export const handleFileDownload = async (
  url: string | undefined,
  fileName: string,
): Promise<void> => {
  if (!url) return;
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Failed to download file");
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, "_blank");
  }
};

/** Get MUI icon component for file type */
export const getFileIcon = (type: "DOCX" | "MP3" | "PDF") => {
  switch (type) {
    case "MP3":
      return AudioFile;
    case "PDF":
      return PictureAsPdf;
    default:
      return Description;
  }
};
